import { TTypedTFunction } from '@lib/useTypedTranslation';

export type DataUsagePeriod = 'training' | 'recent' | 'anomaly';

// Each period's label is a whole-phrase, per-locale template (e.g. en "Training {{usage}}", fr
// "{{usage}} d'entraînement") that wraps a complete usage phrase passed as {{usage}} - it is NOT a
// period word this code joins onto a usage phrase. Joining the two here, in one fixed order, only
// reads correctly in English: French and Spanish place the period qualifier after the noun phrase
// it describes, so each locale has to own where {{usage}} sits.
const PERIOD_LABEL_KEYS = {
  training: 'DATA_USAGE_TRAINING_PERIOD',
  recent: 'DATA_USAGE_RECENT_PERIOD',
  anomaly: 'DATA_USAGE_ANOMALY_PERIOD'
} as const;

export interface DataUsagePeriodLabels {
  /** The period's single, combined legend row (e.g. "Training Data Usage"). */
  legend: string,
  /** The period's mobile series description, shown in its tooltip row (e.g. "Training Mobile Data Usage"). */
  mobile: string,
  /** The period's wifi series description, shown in its tooltip row (e.g. "Training Wi-Fi Data Usage"). */
  wifi: string
}

/**
 * Builds the legend row and the mobile/wifi tooltip rows for one period of the Data Usage anomaly
 * chart. The usage phrases (Data/Mobile Data/Wi-Fi Data Usage) are the same translations the rest
 * of the app already uses, so any locale-specific override of them (e.g. en-US "Cellular Data
 * Usage") carries through into these labels.
 */
export function getDataUsagePeriodLabels(
  period: DataUsagePeriod,
  t: TTypedTFunction<'anomalyDetectionBeta'>,
  tBase: TTypedTFunction<'translation' | 'performance'>
): DataUsagePeriodLabels {
  const periodKey = PERIOD_LABEL_KEYS[period];
  return {
    legend: t(periodKey, { usage: t('DATA_USAGE') }),
    mobile: t(periodKey, { usage: tBase('MOBILE_DATA_USAGE', { ns: 'performance' }) }),
    wifi: t(periodKey, { usage: tBase('WIFI_DATA_USAGE', { ns: 'performance' }) })
  };
}
